/**
 * Recognising traffic from the Checkly E2E runner by its User-Agent (CUR-1111).
 *
 * Checkly browser checks append `CHECKLY_UA_SUFFIX` (`Checkly/<token>`) to the browser's
 * User-Agent so the staging WAF lets them through (playwright.config.ts). Every page load, tRPC
 * call and browser error from a run carries it, whichever account the run logs in as. It is not
 * always the last word: on the suite's own browser contexts Checkly appends its marker after it
 * (`… Checkly/<token> (Checkly, https://www.checklyhq.com)`), so it is matched anywhere, as the
 * WAF rule's `contains` does. The server and the browser tag those Sentry events
 * {@link E2E_TRAFFIC_TAG}, so an alert rule can route E2E failures away from the paging alert while
 * they stay in Sentry as the regression signal they are.
 *
 * A User-Agent is caller-controlled, so this only labels. Nothing is dropped or trusted on it:
 * suppressing a deliberate rejection still needs the verified lifecycle secret
 * (`server/src/utils/observability/e2e-caller.ts`).
 */

/** Sentry tag key and value. Alert rules filter on `e2e` equals `true`, so both are a contract. */
export const E2E_TRAFFIC_TAG = 'e2e';
export const E2E_TRAFFIC_TAG_VALUE = 'true';

const CHECKLY_UA_TOKEN_PREFIX = 'Checkly/';

/**
 * Whether the User-Agent carries a `Checkly/<…>` word, wherever it sits. That covers the suite's
 * `Checkly/<token>` and, deliberately, Checkly's own API and multistep User-Agent
 * (`Checkly/1.0 (https://www.checklyhq.com)`): both are synthetic Checkly traffic, never a real user.
 */
export function hasChecklyUserAgentToken(userAgent: string | undefined): boolean {
    if (!userAgent) return false;
    return userAgent
        .split(' ')
        .some((word) => word.startsWith(CHECKLY_UA_TOKEN_PREFIX) && word.length > CHECKLY_UA_TOKEN_PREFIX.length);
}
